/**
 * La lista de precios en una sola pantalla: de lo que leyó la IA a «Actualizar».
 *
 * Reemplaza la tabla con «Actualizar / Crear / Ignorar» por fila. En producción
 * se aplicó un solo renglón en dos meses: lo reconocido por nombre venía en
 * «Ignorar» y había que tocarlo de a uno (48 veces en los perfumes). Salió del
 * prototipo que probaron usuarios simulados el 29/10/2026, igual que la compra
 * en una pantalla:
 *
 *   · Arriba, el proveedor que dice el archivo; si no lo dice, se elige (sin
 *     proveedor, lo vinculado no se reconoce por código la próxima vez).
 *   · Lo muy parecido se confirma en bloque; lo poco parecido, de a uno.
 *   · A la vista queda solo lo que sube mucho (suele ser un decimal mal leído).
 *     Lo demás va en grupos plegados: suben, bajaron, sin cambios, a mano.
 *   · Lo que no está en el catálogo queda afuera: la lista solo actualiza costos.
 *   · El botón cuenta lo que cambia. Ningún precio baja; hay Deshacer.
 */
import { useMemo, useState } from "react";
import { ArrowRight, ChevronRight } from "lucide-react";

import { Importe } from "@/components/money";
import { Aviso } from "@/components/ui/aviso";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { InputImporte } from "@/components/money";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import { aNumero, type Proveedor } from "@/lib/tipos";

import type { CambiosFila } from "./consultas";
import {
  TEXTO_MARCA_LISTA,
  cambiaElCosto,
  esParecidoSinConfirmar,
  marcaDeFilaLista,
  vaEnBloque,
  variacionDeCosto,
  type FilaRevision,
  type ImportLista,
  type MarcaFilaLista,
} from "./logica";

export interface ListaEnUnaPantallaProps {
  imp: ImportLista;
  filas: ReadonlyArray<FilaRevision>;
  proveedores: ReadonlyArray<Pick<Proveedor, "id" | "nombre">>;
  /** El aviso de moneda de siempre (AvisoMoneda), si corresponde. */
  avisoMoneda: React.ReactNode;
  guardando: boolean;
  aplicando: boolean;
  onCambiarProveedor: (proveedorId: number | null) => void;
  onActualizarFila: (filaId: number, cambios: CambiosFila) => Promise<void>;
  onActualizarFilas: (filas: Array<{ id: number } & CambiosFila>) => Promise<void>;
  onBuscarProducto: (fila: FilaRevision) => void;
  onAplicar: () => void;
}

const VARIANTE: Record<
  MarcaFilaLista,
  "destructive" | "warning" | "success" | "secondary" | "outline"
> = {
  SUBE_MUCHO: "destructive",
  SUBE: "warning",
  NO_BAJA: "success",
  SIN_CAMBIOS: "secondary",
  A_MANO: "outline",
  NO_ACTUALIZA: "outline",
  NO_LO_TENES: "outline",
};

const GRUPOS: ReadonlyArray<{
  marca: MarcaFilaLista;
  titulo: (n: number) => string;
  explicacion: string;
}> = [
  {
    marca: "SUBE",
    titulo: (n) => `${n} ${n === 1 ? "sube" : "suben"}`,
    explicacion: "el precio de venta sube con su margen",
  },
  {
    marca: "NO_BAJA",
    titulo: (n) => `${n} ${n === 1 ? "bajó" : "bajaron"} de costo`,
    explicacion: "el precio no baja",
  },
  {
    marca: "A_MANO",
    titulo: (n) => `${n} con precio a mano`,
    explicacion: "se actualiza el costo, el precio no se toca",
  },
  { marca: "SIN_CAMBIOS", titulo: (n) => `${n} sin cambios`, explicacion: "mismo costo" },
  {
    marca: "NO_ACTUALIZA",
    titulo: (n) => `${n} no se ${n === 1 ? "actualiza" : "actualizan"}`,
    explicacion: "los dejaste como están",
  },
  {
    marca: "NO_LO_TENES",
    titulo: (n) => `${n} no ${n === 1 ? "lo tenés" : "los tenés"}`,
    explicacion: "quedan afuera. ¿Alguno lo tenés con otro nombre?",
  },
];

/** Hasta cuantas filas se ven todas, sin grupos. */
const SIN_PLEGAR = 6;

export function ListaEnUnaPantalla(props: ListaEnUnaPantallaProps): JSX.Element {
  const { imp, filas } = props;
  // «Está bien, aumentó» vale para esta visita: es mirar, no un dato.
  const [subasMiradas, setSubasMiradas] = useState<ReadonlySet<number>>(new Set());
  // Destildados del bloque de parecidos: los que NO son.
  const [noSon, setNoSon] = useState<ReadonlySet<number>>(new Set());
  // «No sé / ninguno»: sin proveedor a sabiendas.
  const [sinProveedorElegido, setSinProveedorElegido] = useState(false);
  const [eligiendoProveedor, setEligiendoProveedor] = useState(false);
  const [abierta, setAbierta] = useState<number | null>(null);
  const [corrigiendo, setCorrigiendo] = useState<number | null>(null);
  const [corregidas, setCorregidas] = useState<ReadonlySet<number>>(new Set());

  const proveedor = props.proveedores.find((p) => p.id === imp.proveedor_id) ?? null;
  const faltaProveedor = proveedor === null && !sinProveedorElegido;

  const enBloque = useMemo(() => filas.filter(vaEnBloque), [filas]);
  const preguntas = useMemo(
    () =>
      filas
        .filter((f) => esParecidoSinConfirmar(f) && !vaEnBloque(f))
        .sort((a, b) => a.orden - b.orden),
    [filas],
  );
  const resueltas = useMemo(() => filas.filter((f) => !esParecidoSinConfirmar(f)), [filas]);
  const marcas = useMemo(
    () => new Map(resueltas.map((f) => [f.id, marcaDeFilaLista(f, subasMiradas)])),
    [resueltas, subasMiradas],
  );
  const marca = (f: FilaRevision): MarcaFilaLista => marcas.get(f.id) ?? "SIN_CAMBIOS";

  const plegar = resueltas.length > SIN_PLEGAR;
  // A la vista: lo que sube mucho, y lo que recien se corrigio (para que se vea).
  const aLaVista = plegar
    ? resueltas.filter((f) => marca(f) === "SUBE_MUCHO" || corregidas.has(f.id))
    : resueltas;
  const actualizan = resueltas.filter((f) => f.accion === "ACTUALIZAR" && f.producto_id !== null);
  const cambian = actualizan.filter(cambiaElCosto);
  const quedanIgual = actualizan.length - cambian.length;
  const subenMucho = resueltas.filter((f) => marca(f) === "SUBE_MUCHO");

  const motivo = faltaProveedor
    ? "Elegí el proveedor"
    : enBloque.length > 0
      ? "Confirmá los parecidos"
      : preguntas.length > 0
        ? preguntas.length === 1
          ? "Contestá la pregunta"
          : `Contestá las ${preguntas.length} preguntas`
        : subenMucho.length > 0
          ? subenMucho.length === 1
            ? "Mirá el costo que sube mucho"
            : `Mirá los ${subenMucho.length} costos que suben mucho`
          : cambian.length === 0
            ? "No hay costos que cambien"
            : null;

  async function confirmarBloque(): Promise<void> {
    await props.onActualizarFilas(
      enBloque.map((f) =>
        noSon.has(f.id)
          ? { id: f.id, producto_id: null, accion: "IGNORAR" }
          : { id: f.id, producto_id: f.producto_id, accion: "ACTUALIZAR" },
      ),
    );
    setNoSon(new Set());
  }

  function marcarMirada(ids: number[]): void {
    setSubasMiradas((s) => new Set([...s, ...ids]));
    setAbierta(null);
  }

  const pregunta = preguntas[0] ?? null;

  return (
    <div className="space-y-4">
      <Card padding="normal" className="space-y-3">
        <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2">
          <div className="min-w-0">
            <TituloSeccion nivel="ficha">
              {proveedor
                ? proveedor.nombre
                : sinProveedorElegido
                  ? "Sin proveedor"
                  : "¿De qué proveedor es?"}
            </TituloSeccion>
            <p className="text-sm text-muted-foreground">{imp.archivo_nombre}</p>
          </div>
          {proveedor || sinProveedorElegido ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setEligiendoProveedor((v) => !v)}
            >
              {eligiendoProveedor ? "Listo" : "Cambiar proveedor"}
            </Button>
          ) : null}
        </div>
        {faltaProveedor || eligiendoProveedor ? (
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2">
              {props.proveedores.map((p) => (
                <Button
                  key={p.id}
                  type="button"
                  variant={p.id === imp.proveedor_id ? "default" : "outline"}
                  aria-pressed={p.id === imp.proveedor_id}
                  disabled={props.guardando}
                  onClick={() => {
                    setSinProveedorElegido(false);
                    setEligiendoProveedor(false);
                    props.onCambiarProveedor(p.id);
                  }}
                >
                  {p.nombre}
                </Button>
              ))}
              <Button
                type="button"
                variant="outline"
                disabled={props.guardando}
                onClick={() => {
                  setSinProveedorElegido(true);
                  setEligiendoProveedor(false);
                  if (imp.proveedor_id !== null) props.onCambiarProveedor(null);
                }}
              >
                No sé / ninguno
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              {imp.proveedor_detectado
                ? `El archivo dice «${imp.proveedor_detectado}» y no lo encontramos entre tus proveedores.`
                : "El archivo no dice de qué proveedor es."}{" "}
              Con el proveedor, lo que vincules se reconoce solo en la próxima lista.
            </p>
          </div>
        ) : null}
        {imp.trae_precio_venta ? (
          <p className="text-xs text-muted-foreground">
            La lista trae precios de venta: no se usan. Los productos con precio a mano no cambian
            de precio; el resto sube solo si sube el costo.
          </p>
        ) : null}
      </Card>

      {props.avisoMoneda}

      {enBloque.length > 0 ? (
        <Card padding="normal" className="space-y-3 border-2 border-primary">
          <div>
            <h3 className="font-semibold">
              {enBloque.length === 1
                ? "Encontramos uno parecido. ¿Es este?"
                : `Encontramos ${enBloque.length} parecidos. ¿Son estos?`}
            </h3>
            {enBloque.length > 1 ? (
              <p className="text-sm text-muted-foreground">Destildá el que no sea.</p>
            ) : null}
          </div>
          {enBloque.length === 1 && enBloque[0] ? (
            <p className="text-sm">
              La lista dice «{enBloque[0].descripcion}»:{" "}
              <strong>{enBloque[0].producto_nombre}</strong>
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {enBloque.map((f) => (
                <li key={f.id} className="py-2">
                  <Checkbox
                    checked={!noSon.has(f.id)}
                    onChange={(e) =>
                      setNoSon((s) => {
                        const n = new Set(s);
                        if (e.target.checked) n.delete(f.id);
                        else n.add(f.id);
                        return n;
                      })
                    }
                    label={`${f.producto_nombre ?? ""} · la lista dice «${f.descripcion}»`}
                  />
                </li>
              ))}
            </ul>
          )}
          <div className="flex flex-wrap gap-2">
            <Button type="button" disabled={props.guardando} onClick={() => void confirmarBloque()}>
              {enBloque.length === 1 ? "Sí, es este" : noSon.size === 0 ? "Sí, son estos" : "Listo"}
            </Button>
            {enBloque.length === 1 && enBloque[0] ? (
              <Button
                type="button"
                variant="outline"
                disabled={props.guardando}
                onClick={() => enBloque[0] && props.onBuscarProducto(enBloque[0])}
              >
                Es otro de mis productos
              </Button>
            ) : null}
          </div>
        </Card>
      ) : pregunta ? (
        <Card padding="normal" className="space-y-3 border-2 border-primary">
          {preguntas.length > 1 ? (
            <p className="text-xs text-muted-foreground">1 de {preguntas.length}</p>
          ) : null}
          <p className="text-sm text-muted-foreground">La lista dice «{pregunta.descripcion}»</p>
          <p>
            ¿Es tu <strong>{pregunta.producto_nombre}</strong>?
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              disabled={props.guardando}
              onClick={() =>
                void props.onActualizarFila(pregunta.id, {
                  producto_id: pregunta.producto_id,
                  accion: "ACTUALIZAR",
                })
              }
            >
              Sí
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={props.guardando}
              onClick={() => props.onBuscarProducto(pregunta)}
            >
              Es otro de mis productos
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={props.guardando}
              onClick={() =>
                void props.onActualizarFila(pregunta.id, { producto_id: null, accion: "IGNORAR" })
              }
            >
              No lo tengo
            </Button>
          </div>
        </Card>
      ) : null}

      {aLaVista.length > 0 ? (
        <section className="space-y-1">
          {plegar ? (
            <h3 className="text-sm font-semibold">
              Para mirar{" "}
              <span className="font-normal text-muted-foreground">({aLaVista.length})</span>
            </h3>
          ) : null}
          {subenMucho.length > 1 ? (
            <div className="flex justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => marcarMirada(subenMucho.map((f) => f.id))}
              >
                Están bien todos
              </Button>
            </div>
          ) : null}
          <Card padding="none">
            <ul className="divide-y divide-border">
              {aLaVista.map((f) => (
                <Renglon
                  key={f.id}
                  fila={f}
                  marca={marca(f)}
                  corregida={corregidas.has(f.id)}
                  abierto={abierta === f.id}
                  corrigiendo={corrigiendo === f.id}
                  onAbrir={(a) => {
                    setAbierta(a ? f.id : null);
                    setCorrigiendo(null);
                  }}
                  onCorregir={(a) => setCorrigiendo(a ? f.id : null)}
                  onMirada={() => marcarMirada([f.id])}
                  onCostoCorregido={async (costo) => {
                    await props.onActualizarFila(f.id, { costo });
                    setCorregidas((s) => new Set([...s, f.id]));
                    setCorrigiendo(null);
                    setAbierta(null);
                  }}
                  {...props}
                />
              ))}
            </ul>
          </Card>
        </section>
      ) : null}

      {plegar
        ? GRUPOS.map(({ marca: m, titulo, explicacion }) => {
            const del = resueltas.filter((f) => marca(f) === m && !aLaVista.includes(f));
            if (del.length === 0) return null;
            return (
              <Card key={m} padding="none" variant="muted">
                <details className="group">
                  <summary className="flex min-h-11 cursor-pointer list-none items-center gap-2 px-4 py-3 text-sm">
                    <ChevronRight
                      className="size-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-90"
                      aria-hidden="true"
                    />
                    <span>
                      <strong className="font-semibold">{titulo(del.length)}</strong> ·{" "}
                      {explicacion}
                    </span>
                  </summary>
                  <ul className="divide-y divide-border border-t border-border bg-card">
                    {del.map((f) => (
                      <Renglon
                        key={f.id}
                        fila={f}
                        marca={marca(f)}
                        corregida={corregidas.has(f.id)}
                        abierto={abierta === f.id}
                        corrigiendo={corrigiendo === f.id}
                        onAbrir={(a) => {
                          setAbierta(a ? f.id : null);
                          setCorrigiendo(null);
                        }}
                        onCorregir={(a) => setCorrigiendo(a ? f.id : null)}
                        onMirada={() => marcarMirada([f.id])}
                        onCostoCorregido={async (costo) => {
                          await props.onActualizarFila(f.id, { costo });
                          setCorregidas((s) => new Set([...s, f.id]));
                          setCorrigiendo(null);
                          setAbierta(null);
                        }}
                        {...props}
                      />
                    ))}
                  </ul>
                </details>
              </Card>
            );
          })
        : null}

      <div className="sticky bottom-0 z-10 -mx-4 border-t border-border bg-background px-4 py-3 sm:mx-0 sm:rounded-lg sm:border">
        <div className="flex flex-col items-stretch gap-1 sm:items-end">
          <Button
            type="button"
            size="lg"
            disabled={motivo !== null || props.guardando}
            cargando={props.aplicando}
            onClick={props.onAplicar}
          >
            {motivo ?? `Actualizar ${cambian.length} ${cambian.length === 1 ? "costo" : "costos"}`}
          </Button>
          {motivo === null ? (
            <span className="text-center text-xs text-muted-foreground sm:text-right">
              {quedanIgual > 0
                ? `${quedanIgual} ${quedanIgual === 1 ? "queda igual" : "quedan igual"}. `
                : ""}
              Ningún precio baja. Después podés deshacerlo.
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function Renglon({
  fila,
  marca,
  corregida,
  abierto,
  corrigiendo,
  onAbrir,
  onCorregir,
  onMirada,
  onCostoCorregido,
  ...props
}: ListaEnUnaPantallaProps & {
  fila: FilaRevision;
  marca: MarcaFilaLista;
  corregida: boolean;
  abierto: boolean;
  corrigiendo: boolean;
  onAbrir: (abrir: boolean) => void;
  onCorregir: (abrir: boolean) => void;
  onMirada: () => void;
  onCostoCorregido: (costo: number) => Promise<void>;
}): JSX.Element {
  const [costo, setCosto] = useState<number | null>(null);
  const variacion = variacionDeCosto(fila);
  const nombre = fila.producto_nombre ?? fila.descripcion;
  const pendiente = marca === "SUBE_MUCHO";
  const precioAntes = fila.precio_actual === null ? null : aNumero(fila.precio_actual);
  const precioDespues = fila.precio_resultante === null ? null : aNumero(fila.precio_resultante);
  const moneda = fila.precio_moneda ?? "ARS";

  const precio =
    fila.producto_id === null || precioDespues === null ? null : (
      <span className="inline-flex flex-wrap items-baseline gap-x-1.5 text-sm">
        <span className="text-xs text-muted-foreground">
          {pendiente ? "si lo confirmás" : "vende"}
        </span>
        {precioAntes !== null && precioDespues > precioAntes ? (
          <Importe
            monto={precioAntes}
            moneda={moneda}
            tamano="sm"
            className="text-muted-foreground line-through"
          />
        ) : null}
        {/* Lo que sube mucho todavía no está confirmado: el precio va en gris. */}
        <Importe
          monto={precioDespues}
          moneda={moneda}
          className={pendiente ? "text-muted-foreground" : "font-semibold"}
        />
      </span>
    );

  return (
    <li className="px-3 py-3 sm:px-4">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-sm font-medium">{nombre}</span>
            <Badge variant={VARIANTE[marca]}>{TEXTO_MARCA_LISTA[marca]}</Badge>
            {corregida ? <Badge variant="info">costo corregido</Badge> : null}
          </div>
          <div className="flex flex-wrap gap-x-1 text-xs text-muted-foreground">
            {fila.producto_nombre !== null && fila.producto_nombre !== fila.descripcion ? (
              <span>lista: «{fila.descripcion}» ·</span>
            ) : null}
            {fila.costo_anterior !== null && fila.producto_id !== null ? (
              <span className="whitespace-nowrap">
                costo{" "}
                <Importe
                  monto={aNumero(fila.costo_anterior)}
                  moneda={fila.moneda_anterior ?? fila.moneda}
                  tamano="sm"
                />{" "}
                <ArrowRight className="inline size-3" aria-label="pasa a" />{" "}
                <Importe monto={aNumero(fila.costo)} moneda={fila.moneda} tamano="sm" />
                {variacion !== null && Math.abs(variacion) >= 0.005
                  ? ` (${variacion > 0 ? "+" : ""}${Math.round(variacion * 100)} %)`
                  : ""}
              </span>
            ) : (
              <span>
                costo de la lista{" "}
                <Importe monto={aNumero(fila.costo)} moneda={fila.moneda} tamano="sm" />
              </span>
            )}
          </div>
          {precio ? <div className="sm:hidden">{precio}</div> : null}
        </div>
        {precio ? <div className="hidden pt-0.5 text-right sm:block">{precio}</div> : null}
        <Button
          type="button"
          variant={pendiente ? "default" : "outline"}
          size="sm"
          className="shrink-0"
          aria-expanded={abierto}
          onClick={() =>
            marca === "NO_LO_TENES" ? props.onBuscarProducto(fila) : onAbrir(!abierto)
          }
        >
          {abierto
            ? "Cerrar"
            : marca === "NO_LO_TENES"
              ? "Lo tengo"
              : pendiente
                ? "Revisar"
                : "Cambiar"}
        </Button>
      </div>

      {abierto ? (
        <Card variant="muted" padding="compacto" className="mt-3 space-y-3">
          {pendiente && variacion !== null ? (
            <p className="text-sm">
              Sube {Math.round(variacion * 100)} %. Suele ser un decimal mal leído: fijate en la
              lista.
            </p>
          ) : null}
          {corrigiendo ? (
            <div className="flex flex-wrap items-end gap-2">
              <div className="w-48">
                <InputImporte
                  valor={costo}
                  onValorChange={setCosto}
                  moneda={fila.moneda}
                  label="Costo correcto"
                  autoFocus
                />
              </div>
              <Button
                type="button"
                size="sm"
                disabled={costo === null || costo <= 0 || props.guardando}
                onClick={() => costo !== null && void onCostoCorregido(costo)}
              >
                Guardar
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => onCorregir(false)}>
                Cancelar
              </Button>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {pendiente ? (
                <Button type="button" size="sm" onClick={onMirada}>
                  Está bien, aumentó
                </Button>
              ) : null}
              <Button type="button" variant="outline" size="sm" onClick={() => onCorregir(true)}>
                Corregir el costo
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => props.onBuscarProducto(fila)}
              >
                Es otro de mis productos
              </Button>
              {fila.accion === "ACTUALIZAR" ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={props.guardando}
                  onClick={() => void props.onActualizarFila(fila.id, { accion: "IGNORAR" })}
                >
                  No actualizar este
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={props.guardando}
                  onClick={() => void props.onActualizarFila(fila.id, { accion: "ACTUALIZAR" })}
                >
                  Sí, actualizarlo
                </Button>
              )}
            </div>
          )}
        </Card>
      ) : null}
    </li>
  );
}

/**
 * Cuando el archivo es un comprobante de compra (una factura, una cotización),
 * se ofrece pasarlo a Compras: ahí suma stock y deuda. Se subieron dos así en
 * producción, y nada avisaba.
 */
export function AvisoComprobante({
  imp,
  pasando,
  onPasarACompras,
  onEsUnaLista,
}: {
  imp: ImportLista;
  pasando: boolean;
  onPasarACompras: () => void;
  onEsUnaLista: () => void;
}): JSX.Element {
  return (
    <Aviso
      tono="warning"
      accion={
        <div className="flex flex-wrap gap-2">
          <Button type="button" size="sm" cargando={pasando} onClick={onPasarACompras}>
            Pasarla a Compras
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={onEsUnaLista}>
            No, es una lista de precios
          </Button>
        </div>
      }
    >
      Esto parece una compra, no una lista de precios
      {imp.comprobante_detectado ? ` (dice «${imp.comprobante_detectado}»)` : ""}. Las compras se
      cargan en Compras: ahí suma el stock y la deuda con el proveedor. Acá no se tocó nada.
    </Aviso>
  );
}
